import React, { useEffect, useState } from "react";
import { Card, Form, Switch, InputNumber, Input, notification } from "antd";
import OptionService from "@Services/OptionService";
import { IOptionDefinitionDetails, IOptionInstanceDetails } from "@Models/IOptionDefinitionDetails";
import SessionManager from "@Core/session";
import { SkeletonRows } from "@Components/shared/SkeletonRows";
import MiniSpinner from "@Components/shared/MiniSpinner";
import "@Styles/components/accountOptionsForm.scss";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@Constants";

const AccountOptionsTab = () => {
    const [userDefinitions, setUserDefinitions] = useState<IOptionDefinitionDetails[]>(null);
    const [userInstances, setUserInstances] = useState<IOptionInstanceDetails[]>([]);
    const [isFetching, setFetching] = useState(false);
    const [isUpdating, setUpdating] = useState(false);
    const queryClient = useQueryClient();

    /**
     * Update a user option
     * @param optionId
     * @param value
     */
    const updateUserOption = async (optionId: string, value: string) => {
        setUpdating(true);
        const service = new OptionService();
        const newOption: IOptionInstanceDetails = {
            instanceId: SessionManager.getServiceUser()?.userId,
            optionId: optionId,
            value: value,
        };
        await service.updateUser(SessionManager.getServiceUser()?.userId, newOption);
        const newInstances = [...userInstances];
        const index = newInstances.findIndex(
            (x) => x.optionId === optionId && x.instanceId === SessionManager.getServiceUser()?.userId
        );
        if (index !== -1) {
            newInstances[index] = newOption;
            setUserInstances(newInstances);
        }
        setUpdating(false);
        notification.success({ message: "Account option updated." });
        await queryClient.invalidateQueries([QueryKeys.UserOptions]);
    };

    /**
     * Fetch the user option definitions and the instances for this user.
     */
    const fetchUserOptions = async () => {
        setFetching(true);
        const service = new OptionService();
        const definitionsResult = await service.getUserDefinitions();
        if (!definitionsResult.hasErrors) {
            setUserDefinitions(definitionsResult.value);
            const instanceResult = await service.getUser(SessionManager.getServiceUser()?.userId);
            if (!instanceResult.hasErrors) {
                setUserInstances(instanceResult.value);
            }
        }
        setFetching(false);
    };

    useEffect(() => {
        fetchUserOptions().then();
    }, []);

    /**
     * Render a user option, falling back to the definition default where required.
     * @param option
     */
    const renderOption = (option: IOptionDefinitionDetails) => {
        let value = "";
        const index = userInstances.findIndex((x) => x.optionId === option.optionId);
        value = index === -1 ? option.defaultValue : userInstances[index].value;

        switch (option.type.toLowerCase()) {
            case "bool":
                return (
                    <Switch
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                        defaultChecked={value === "true"}
                        onChange={(checked) => updateUserOption(option.optionId, checked ? "true" : "false")}
                    />
                );
            case "int":
                return (
                    <InputNumber
                        defaultValue={value}
                        onChange={(value) => updateUserOption(option.optionId, value.toString())}
                    />
                );
            case "string":
                return (
                    <Input defaultValue={value} onChange={(e) => updateUserOption(option.optionId, e.target.value)} />
                );
            case "user":
                break;
            case "boollist":
                break;
            case "intlist":
                break;
            case "stringlist":
                break;
            case "userlist":
                break;
        }
        return null;
    };

    return (
        <Card
            title={
                <>
                    Account Options &nbsp;&nbsp;
                    {isUpdating && (
                        <span
                            key={"spin-item-searching"}
                            style={{ display: "inline-block", justifyContent: "left", paddingLeft: 10 }}
                        >
                            <MiniSpinner />
                        </span>
                    )}
                </>
            }
            className="newUI-raised"
        >
            {isFetching ? (
                <SkeletonRows />
            ) : (
                <Form className={"account-options-form"}>
                    <table>
                        <tbody>
                            {userDefinitions
                                ?.sort(function (a, b) {
                                    return a.precedence - b.precedence;
                                })
                                .map((x, index) => (
                                    <tr key={index}>
                                        <td>{x.label}</td>
                                        <td>
                                            <Form.Item>{renderOption(x)}</Form.Item>
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </Form>
            )}
        </Card>
    );
};

export default AccountOptionsTab;
